import { useFlags } from './useFlagsWithLocalOverrides';
import { useGetCurrentTenantProfileQuery } from '@/modules/services/auth';

/**
 * AI feature gates for Course Builder (LMS2-5047). Each entry point requires its
 * LaunchDarkly rollout flag AND the company-level "Allow AI Features" toggle
 * (`aiEnabled`, read live from TMS via the tenant profile).
 *
 * Only an explicit `aiEnabled === false` disables AI here. If the field is absent —
 * the API build that returns it isn't deployed yet, or the profile hasn't loaded —
 * we fall back to the LaunchDarkly flags alone and let the server-side guard be the
 * real gate. This lets the frontend deploy ahead of the API without hiding AI
 * features for every tenant, while an explicit toggle-off still hides them.
 *
 * Centralised so every AI surface applies the company toggle consistently — a new
 * surface must not re-derive the LD flag without the company-toggle check.
 */
export const useAiFeatureFlags = (): {
  isPptConversionEnabled: boolean;
  isCreateWithAiEnabled: boolean;
} => {
  const {
    learningElaAiPptConversionEnable: ldPptConversionEnabled,
    learningElaCreateWithAi: ldCreateWithAiEnabled,
  } = useFlags();
  const { data: currentTenant } = useGetCurrentTenantProfileQuery();
  const aiFeaturesDisabled = currentTenant?.aiEnabled === false;

  return {
    isPptConversionEnabled: Boolean(
      ldPptConversionEnabled && !aiFeaturesDisabled,
    ),
    isCreateWithAiEnabled: Boolean(
      ldCreateWithAiEnabled && !aiFeaturesDisabled,
    ),
  };
};
